import AsyncStorage from '@react-native-async-storage/async-storage';
import { clearPilotTelemetryStorage } from './pilotTelemetryStore';
import {
  clearStoredAuthToken,
  getStoredAuthToken,
  setStoredAuthToken,
} from './authTokenStore';

const AUTH_META_KEY = '@dengfeng/auth/session-meta';
const SECURE_STORE_TIMEOUT_MS = 8000;

type AuthSessionMeta = {
  tokenType: string;
  authenticatedAt: string;
};

export type AuthSessionState = {
  isAuthenticated: boolean;
  tokenType: string | null;
  authenticatedAt: string | null;
};

type AuthSessionListener = (state: AuthSessionState) => void;

const defaultSessionState: AuthSessionState = {
  isAuthenticated: false,
  tokenType: null,
  authenticatedAt: null,
};

const authSessionListeners = new Set<AuthSessionListener>();

const notifyAuthSessionListeners = (state: AuthSessionState): void => {
  authSessionListeners.forEach((listener) => listener(state));
};

export const subscribeAuthSession = (listener: AuthSessionListener): (() => void) => {
  authSessionListeners.add(listener);
  return () => authSessionListeners.delete(listener);
};

const normalizeTokenType = (value: string | null | undefined): string =>
  value && value.trim() ? value.trim() : 'bearer';

const withSecureStoreTimeout = async <T>(operation: Promise<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race([
      operation,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('SecureStore operation timed out')), SECURE_STORE_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const readMeta = async (): Promise<AuthSessionMeta | null> => {
  try {
    const rawMeta = await AsyncStorage.getItem(AUTH_META_KEY);
    if (!rawMeta) {
      return null;
    }
    const parsed = JSON.parse(rawMeta) as Partial<AuthSessionMeta>;
    if (typeof parsed.authenticatedAt !== 'string') {
      return null;
    }
    return {
      tokenType: normalizeTokenType(parsed.tokenType),
      authenticatedAt: parsed.authenticatedAt,
    };
  } catch {
    return null;
  }
};

export const getStoredAccessToken = async (): Promise<string | null> => {
  try {
    return await withSecureStoreTimeout(getStoredAuthToken());
  } catch {
    return null;
  }
};

export const getAuthSessionState = async (): Promise<AuthSessionState> => {
  const [token, meta] = await Promise.all([getStoredAccessToken(), readMeta()]);
  if (!token) {
    return defaultSessionState;
  }
  return {
    isAuthenticated: true,
    tokenType: meta?.tokenType ?? 'bearer',
    authenticatedAt: meta?.authenticatedAt ?? null,
  };
};

export const saveAuthSession = async ({
  accessToken,
  tokenType,
}: {
  accessToken: string;
  tokenType?: string | null;
}): Promise<AuthSessionState> => {
  const authenticatedAt = new Date().toISOString();
  const nextMeta: AuthSessionMeta = {
    tokenType: normalizeTokenType(tokenType),
    authenticatedAt,
  };
  await withSecureStoreTimeout(setStoredAuthToken(accessToken));
  await AsyncStorage.setItem(AUTH_META_KEY, JSON.stringify(nextMeta));
  const nextState = {
    isAuthenticated: true,
    tokenType: nextMeta.tokenType,
    authenticatedAt,
  };
  notifyAuthSessionListeners(nextState);
  return nextState;
};

export const clearAuthSession = async (): Promise<void> => {
  await clearPilotTelemetryStorage();
  await Promise.all([
    withSecureStoreTimeout(clearStoredAuthToken()).catch(() => undefined),
    AsyncStorage.removeItem(AUTH_META_KEY),
  ]);
  notifyAuthSessionListeners(defaultSessionState);
};
